// src/pages/Insights/Articles/posts/ArticlePosts.ts
// Consolidated list of all high-quality articles with local image assets
// No imports needed for images, using direct string paths for public assets
import { Article } from '../../types';


// Import responsive assets manifest
import { responsiveManifest } from './responsive-assets';

/**
 * Helper to generate srcSet from manifest versions
 */
const getSrcSet = (versions: Record<string, any>) => {
    return Object.entries(versions)
        .filter(([key]) => !isNaN(Number(key))) // Only include width keys (200, 400, etc)
        .map(([width, url]) => `${url} ${width}w`)
        .join(', ');
};

const allPosts: Article[] = [
    {
        id: 'data-format-comparison',
        title: 'JSON vs. XML vs. YAML: Data Format Comparison',
        slug: 'json-xml-yaml-data-format-comparison',
        category: 'technology-innovation',
        date: 'December 30, 2025',
        readTime: 17,
        author: 'FileFusion',
        imageUrl: '/assets/images/articles/responsive/data-format-comparison-1200.webp',
        srcSet: getSrcSet(responsiveManifest["data-format-comparison"]),
        placeholder: responsiveManifest["data-format-comparison"]["placeholder"],
        excerpt: 'Compare JSON, XML, and YAML data formats. Learn their strengths, weaknesses, performance characteristics, and ideal use cases. Discover which format is best for APIs, configs, and data exchange.',
        keywords: ['JSON vs XML', 'YAML comparison', 'data formats', 'JSON XML YAML', 'data serialization', 'configuration files', 'API data format', 'data interchange', 'markup languages', 'structured data', 'data format comparison', 'REST API formats', 'XML vs JSON performance', 'YAML syntax', 'data validation'],
        featured: true,
        hasFullContent: true,
        contentFile: 'DataFormatComparison'
    },
    {
        id: 'barcode-scanner-technology',
        title: 'How Barcode Scanners Work: Laser, CCD, and Camera',
        slug: 'how-barcode-scanners-work-laser-ccd-camera',
        category: 'technology-innovation',
        date: 'December 30, 2025',
        readTime: 19,
        author: 'FileFusion',
        imageUrl: '/assets/images/articles/responsive/barcode-scanner-technology-1200.webp',
        srcSet: getSrcSet(responsiveManifest["barcode-scanner-technology"]),
        placeholder: responsiveManifest["barcode-scanner-technology"]["placeholder"],
        excerpt: 'Explore the technology behind barcode scanners. Learn how laser, CCD, and camera-based scanners decode barcodes using optics, sensors, and signal processing. Discover which technology is best for your needs.',
        keywords: ['barcode scanner', 'laser scanner', 'CCD scanner', 'camera scanner', 'barcode technology', 'how scanners work', 'barcode reading', 'optical scanning', 'barcode decoder', 'scanner comparison', 'barcode symbology', 'image-based scanning', 'retail scanning', 'industrial scanners', 'scanner types'],
        featured: true,
        hasFullContent: true,
        contentFile: 'BarcodeScannerTechnology'
    },
    {
        id: 'qr-code-anatomy',
        title: 'QR Code Anatomy: Finder Patterns & Error Correction',
        slug: 'qr-code-anatomy-structure-error-correction',
        category: 'technology-innovation',
        date: 'December 30, 2025',
        readTime: 20,
        author: 'FileFusion',
        imageUrl: '/assets/images/articles/responsive/qr-code-anatomy-1200.webp',
        srcSet: getSrcSet(responsiveManifest["qr-code-anatomy"]),
        placeholder: responsiveManifest["qr-code-anatomy"]["placeholder"],
        excerpt: 'Dive deep into the fascinating structure of QR codes. Learn how finder patterns enable rapid detection, timing patterns correct distortion, and Reed-Solomon error correction makes codes resilient to damage.',
        keywords: ['QR code structure', 'QR code anatomy', 'finder patterns', 'timing patterns', 'error correction', 'Reed-Solomon', 'QR code technology', 'barcode encoding', '2D barcode', 'QR code masking', 'data encoding', 'format information', 'alignment patterns', 'QR code versions', 'error correction levels'],
        featured: true,
        hasFullContent: true,
        contentFile: 'QRCodeAnatomy'
    },
    {
        id: 'diff-algorithms-explained',
        title: 'Diff Algorithms: Myers vs Hunt-McIlroy vs Patience',
        slug: 'diff-algorithms-myers-patience-explained',
        category: 'guides-how-tos',
        date: 'December 30, 2025',
        readTime: 19,
        author: 'FileFusion',
        imageUrl: '/assets/images/articles/responsive/diff-algorithms-explained-1200.webp',
        srcSet: getSrcSet(responsiveManifest["diff-algorithms-explained"]),
        placeholder: responsiveManifest["diff-algorithms-explained"]["placeholder"],
        excerpt: 'Explore the algorithms behind version control and text comparison. Learn how Myers, Hunt-McIlroy, and Patience diff algorithms work, their trade-offs, and when to use each approach.',
        keywords: ['diff algorithm', 'Myers algorithm', 'Patience diff', 'text comparison', 'version control', 'git diff', 'longest common subsequence', 'LCS algorithm', 'edit distance', 'code comparison', 'Hunt-McIlroy', 'text diff', 'file comparison', 'algorithm analysis', 'dynamic programming'],
        featured: true,
        hasFullContent: true,
        contentFile: 'DiffAlgorithmsExplained'
    },
    {
        id: 'background-removal-quality',
        title: 'Background Removal Quality: AI Issues & Fixes',
        slug: 'background-removal-quality-ai-fixes',
        category: 'guides-how-tos',
        date: 'December 30, 2025',
        readTime: 20,
        author: 'FileFusion',
        imageUrl: '/assets/images/articles/responsive/background-removal-quality-1200.webp',
        srcSet: getSrcSet(responsiveManifest["background-removal-quality"]),
        placeholder: responsiveManifest["background-removal-quality"]["placeholder"],
        excerpt: 'Learn why AI background removal fails on hair, transparent objects, and complex scenes. Discover manual refinement techniques, preventive measures, and best practices for professional-quality results.',
        keywords: ['background removal', 'AI background removal', 'remove background', 'image segmentation', 'hair cutout', 'transparent PNG', 'photo editing', 'image matting', 'alpha channel', 'edge refinement', 'color decontamination', 'product photography', 'portrait editing', 'background remover', 'image masking'],
        featured: true,
        hasFullContent: true,
        contentFile: 'BackgroundRemovalQuality'
    },
    {
        id: 'pdf-file-structure',
        title: 'PDF Structure: Pages, Objects, and Cross-References',
        slug: 'pdf-file-structure-pages-objects-xref',
        category: 'guides-how-tos',
        date: 'December 30, 2025',
        readTime: 21,
        author: 'FileFusion',
        imageUrl: '/assets/images/articles/responsive/pdf-file-structure-1200.webp',
        srcSet: getSrcSet(responsiveManifest["pdf-file-structure"]),
        placeholder: responsiveManifest["pdf-file-structure"]["placeholder"],
        excerpt: 'Explore the internal structure of PDF files. Learn how objects, page trees, cross-reference tables, and incremental updates work together to create the world\'s most important document format.',
        keywords: ['PDF structure', 'PDF format', 'PDF objects', 'cross-reference table', 'PDF xref', 'PDF pages', 'PDF internals', 'how PDF works', 'PDF file format', 'PDF specification', 'PDF compression', 'PDF merge', 'PDF split', 'document structure', 'PDF optimization'],
        featured: true,
        hasFullContent: true,
        contentFile: 'PDFFileStructure'
    },
    {
        id: 'ocr-technology-explained',
        title: 'How Optical Character Recognition (OCR) Works',
        slug: 'how-optical-character-recognition-works-ocr',
        category: 'guides-how-tos',
        date: 'December 30, 2025',
        readTime: 22,
        author: 'FileFusion',
        imageUrl: '/assets/images/articles/responsive/ocr-technology-explained-1200.webp',
        srcSet: getSrcSet(responsiveManifest["ocr-technology-explained"]),
        placeholder: responsiveManifest["ocr-technology-explained"]["placeholder"],
        excerpt: 'Discover the fascinating technology behind OCR. Learn how computers convert images of text into editable digital text through preprocessing, segmentation, pattern recognition, and machine learning.',
        keywords: ['OCR technology', 'optical character recognition', 'text recognition', 'image to text', 'OCR explained', 'character recognition', 'document scanning', 'text extraction', 'OCR accuracy', 'deep learning OCR', 'Tesseract OCR', 'image preprocessing', 'character segmentation', 'pattern recognition', 'OCR algorithms'],
        featured: true,
        hasFullContent: true,
        contentFile: 'OCRTechnologyExplained'
    },
    {
        id: 'image-compression-physics',
        title: 'Image Compression Physics: JPEG, PNG, and WebP',
        slug: 'image-compression-physics-jpeg-png-webp',
        category: 'guides-how-tos',
        date: 'December 30, 2025',
        readTime: 18,
        author: 'FileFusion',
        imageUrl: '/assets/images/articles/responsive/image-compression-physics-1200.webp',
        srcSet: getSrcSet(responsiveManifest["image-compression-physics"]),
        placeholder: responsiveManifest["image-compression-physics"]["placeholder"],
        excerpt: 'Explore the mathematics and physics behind image compression. Learn how JPEG, PNG, and WebP work, when to use each format, and how to achieve optimal file sizes without sacrificing quality.',
        keywords: ['image compression', 'JPEG compression', 'PNG compression', 'WebP format', 'lossy compression', 'lossless compression', 'DCT transform', 'image optimization', 'file size reduction', 'chroma subsampling', 'image quality', 'compression algorithms', 'image formats comparison', 'web image optimization', 'photo compression'],
        featured: true,
        hasFullContent: true,
        contentFile: 'ImageCompressionPhysics'
    }
];

export default allPosts;
